import { queryOptions } from "@tanstack/react-query";

import {
	apiPaths,
	notificationPath,
	notificationSchema,
	notificationsPageSchema,
	type Notification,
	type NotificationKind,
	type NotificationState,
} from "@ravnar/api-contract";

import { ApiError, apiRequest } from "./client";

// The bell's requests. Every query key starts with "notifications", so archiving one refreshes the bell and the page.

/** How often the bell asks the API again, so that a notification raised while nothing on the page asked reaches it. */
export const NOTIFICATIONS_REFETCH_INTERVAL_MS = 30_000;

export interface NotificationsPageRequest {
	/** The unattended ones, which the bell counts, unless the Archived tab asks for the others. */
	readonly state?: NotificationState;
	/** Only the notifications of this kind, which a filter of the Notifications page asks for; every kind when none. */
	readonly kind?: NotificationKind;
	/** A page link from an earlier page; the newest page when there is none. */
	readonly page?: string;
}

/**
 * A page of the member's notifications, newest first, either unattended or archived, of one kind or of every kind, with
 * the count of what it lists, the state's notifications counted by kind, and links to the pages beside it. The bell
 * asks for the newest unattended page on every page of the app, so a list a few seconds old is fine there, and that one
 * page is asked for again every half minute: the intake copier raises a notification seconds after an upload lands,
 * when no request of the page is on its way. No other page is polled — page three of the archived ones does not change
 * while it is read.
 */
export const notificationsQueryOptions = ({ state = "unattended", kind, page }: NotificationsPageRequest = {}) =>
	queryOptions({
		queryKey: ["notifications", { state, kind, page }],
		queryFn: ({ signal }) =>
			apiRequest(apiPaths.notifications, notificationsPageSchema, {
				query: { state, ...(kind !== undefined && { kind }), ...(page !== undefined && { page }) },
				signal,
			}),
		staleTime: 15_000,
		refetchInterval:
			state === "unattended" && kind === undefined && page === undefined
				? NOTIFICATIONS_REFETCH_INTERVAL_MS
				: false,
		// A refused page link will not change on a retry, and the page redirects on it straight away.
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

export interface SetNotificationArchivedRequest {
	readonly id: string;
	readonly archived: boolean;
}

/** Archives one of the member's notifications, or restores it, and answers it as the API keeps it. Fails with 404 when it is gone. */
export function setNotificationArchived({ id, archived }: SetNotificationArchivedRequest): Promise<Notification> {
	return apiRequest(notificationPath(id), notificationSchema, { method: "PATCH", body: { archived } });
}
